import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { MapEntitiesSet } from "@mapmycustomers/shared/types/map";

import { Feature } from "@app/component/map/FeaturedMap";
import MarkerEffect from "@app/enum/MarkerEffect";
import MapTooltip from "@app/scene/map/components/MapTooltip";
import { getLeadAnnotation } from "@app/scene/map/store/leadFinder/selectors";
import { setPinHover } from "@app/scene/map/store/mapMode/actions";
import {
  getHighlightedRecords,
  getMapViewTool,
  getPinHover,
  getSelectedRecords,
} from "@app/scene/map/store/mapMode/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import loggingService from "@app/util/logging";
import { BASE_LEAD_ICON_ZINDEX } from "@app/util/map/consts";
import { leadIdGetter } from "@app/util/map/idGetters";
import getResultingSvg from "@app/util/map/markerStyles/getResultingSvg";
import getSvgEffect from "@app/util/map/markerStyles/getSvgEffect";
import getSvgForShape from "@app/util/map/markerStyles/getSvgForShape";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

const leadGeometryGetter = (
  lead: Lead
): Exclude<google.maps.Data.FeatureOptions["geometry"], undefined> =>
  new google.maps.Data.Point(lead.geometry.location);

interface Props {
  annotation?: Lead["place_id"];
  highlight: MapEntitiesSet;
  interactive: boolean;
  leads: Lead[];
  onClick?: (entity: Lead) => void;
  pinHover?: string;
  selection: Set<string>;
  setPinHover: typeof setPinHover;
  styleGetter?: (item: Lead) => google.maps.Data.StyleOptions;
}

const LeadFeature: React.FC<Props> = ({
  annotation,
  highlight,
  interactive,
  leads,
  onClick,
  pinHover,
  selection,
  setPinHover,
  styleGetter,
}) => {
  const analytics = useAnalytics();

  const handleEntityClick = useCallback(
    (lead: Lead, event: google.maps.MapMouseEvent) => {
      loggingService.debug("lead clicked", { event, lead });
      analytics.clicked(["Lead Pin"]);
      onClick?.(lead);
    },
    [analytics, onClick]
  );

  const handleEntityMouseOver = useCallback(
    (lead: Lead) => {
      if (interactive && lead) {
        setPinHover(leadIdGetter(lead));
      }
    },
    [interactive, setPinHover]
  );

  const handleEntityMouseLeave = useCallback(() => {
    if (interactive) {
      setPinHover(undefined);
    }
  }, [interactive, setPinHover]);

  const lead: Lead | undefined = useMemo(() => {
    if (interactive && pinHover) {
      return leads.find(
        (leadPin) => leadPin.place_id === pinHover && leadPin.place_id !== annotation
      );
    }

    return undefined;
  }, [annotation, interactive, leads, pinHover]);

  const leadStyleGetter = useCallback(
    (lead: Lead): google.maps.Data.StyleOptions => {
      const hasPotentialDuplicates = !!lead.mmcRecords?.length;

      let icon = lead.blackListed
        ? MarkerShape.LEAD_HIDDEN
        : hasPotentialDuplicates
        ? MarkerShape.LEAD_DUPLICATE
        : MarkerShape.LEAD;
      let zIndex = BASE_LEAD_ICON_ZINDEX;

      const leadId = leadIdGetter(lead);
      if (selection.has(leadId)) {
        icon = MarkerShape.LEAD_SELECTED;
        zIndex++;
      } else if (annotation === leadId || highlight.has(leadId)) {
        icon = lead.blackListed
          ? MarkerShape.LEAD_HIDDEN_CLICKED
          : hasPotentialDuplicates
          ? MarkerShape.LEAD_DUPLICATE_CLICKED
          : MarkerShape.LEAD_CLICKED;
        zIndex += 2;
      }
      const { shapeAnchor, shapeSize, shapeSvg } = getSvgForShape(icon, "", "");

      const shapeEffect = getSvgEffect(MarkerEffect.NONE, shapeSize);

      const { anchor, size, svg } = getResultingSvg(shapeSvg, shapeSize, shapeAnchor, shapeEffect);

      return {
        icon: {
          anchor: new google.maps.Point(...anchor),
          scaledSize: new google.maps.Size(...size),
          url: svgToDataUrl(svg),
        },
        zIndex,
      };
    },
    [annotation, highlight, selection]
  );

  return (
    <>
      <Feature<Lead>
        geometryGetter={leadGeometryGetter}
        idGetter={leadIdGetter}
        items={leads}
        onClick={handleEntityClick}
        onMouseLeave={handleEntityMouseLeave}
        onMouseOver={handleEntityMouseOver}
        styleGetter={styleGetter ?? leadStyleGetter}
        type="leads"
      />
      {interactive && lead && (
        <MapTooltip position={lead?.geometry?.location} tooltip={lead.name} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  annotation: getLeadAnnotation(state),
  highlight: getHighlightedRecords(state),
  interactive: getMapViewTool(state) !== MapTool.LASSO,
  pinHover: getPinHover(state),
  selection: getSelectedRecords(state),
});

const mapDispatchToProps = {
  setPinHover,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadFeature);
